import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, ArrowLeft, Info, RefreshCw } from "lucide-react";
import { useQuestionnaireContext } from "../../context/QuestionnaireContext";
import { PATH_SIGN_IN } from "@/constants";

interface QuestionnaireAlertProps {
  title?: string;
  message?: string;
}

interface AlertContent {
  title: string;
  message?: string;
  showInfo: boolean;
  showRefresh: boolean;
  showLoginAction: boolean;
}

const DEFAULT_ALERT: AlertContent = {
  title: "questionnaires.errors.title",
  message: undefined,
  showInfo: false,
  showRefresh: false,
  showLoginAction: false,
};

const STATIC_ALERTS: Record<string, AlertContent> = {
  invalid_token: {
    title: "questionnaires.errors.invalidTokenTitle",
    message: undefined,
    showInfo: true,
    showRefresh: false,
    showLoginAction: true,
  },
  no_questionnaire: {
    title: "questionnaires.errors.noQuestionnaireTitle",
    message: "questionnaires.errors.noQuestionnaireMessage",
    showInfo: false,
    showRefresh: false,
    showLoginAction: false,
  },
  no_agreement: {
    title: "questionnaires.errors.noAgreementTitle",
    message: "questionnaires.errors.noAgreementMessage",
    showInfo: false,
    showRefresh: false,
    showLoginAction: false,
  },
};

export const QuestionnaireAlert = ({
  title,
  message,
}: QuestionnaireAlertProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clearError, refreshQuestionnaire, errorState, alertReason } =
    useQuestionnaireContext();

  const alertContent = useMemo(() => {
    if (title) {
      return {
        title,
        message,
        showInfo: false,
        showRefresh: false,
        showLoginAction: false,
      };
    }

    if (alertReason === "query_error") {
      return {
        title: "questionnaires.errors.title",
        message: errorState?.error?.message,
        showInfo: false,
        showRefresh: errorState?.isQuery ?? false,
        showLoginAction: false,
      };
    }

    return STATIC_ALERTS[alertReason ?? ""] ?? DEFAULT_ALERT;
  }, [title, message, alertReason, errorState?.error, errorState?.isQuery]);

  const Icon = alertContent.showInfo ? Info : AlertCircle;
  const iconColor = alertContent.showInfo ? "text-blue-500" : "text-red-500";

  const handleBack = useCallback(() => {
    clearError();
  }, [clearError]);

  const handleRefresh = useCallback(() => {
    refreshQuestionnaire();
  }, [refreshQuestionnaire]);

  const handleLogin = useCallback(() => {
    navigate(PATH_SIGN_IN);
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="text-center">
            <Icon className={`h-16 w-16 mx-auto mb-4 ${iconColor}`} />
            <h2 className="text-2xl font-bold mb-6">{t(alertContent.title)}</h2>
            {alertContent.message && (
              <p className="text-gray-600 mb-6">{t(alertContent.message)}</p>
            )}
            {alertContent.showLoginAction ? (
              <Button onClick={handleLogin} className="w-full">
                {t("questionnaires.errors.invalidTokenAction")}
              </Button>
            ) : alertContent.showRefresh ? (
              <Button
                onClick={handleRefresh}
                variant="outline"
                className="w-full"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                {t("common.refresh")}
              </Button>
            ) : (
              <Button onClick={handleBack} variant="outline" className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t("common.back")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
